/**
 * Shared encoding for the hierarchical "Reason for Closure" listing filter (CDS-7443).
 *
 * MultiLevelDropdown stores every selection as a flat string array. We therefore namespace each
 * option value so a single array can carry reason ids, closed-via source ids, and the synthetic
 * "no reason" flag together, and so the parent "group" tokens the component inserts when a whole
 * group is checked can be told apart from real leaf selections and ignored on decode.
 */
export const CLOSURE_PREFIX = {
  REASON: 'reason:',
  SOURCE: 'source:',
};

export const CLOSURE_GROUP = {
  REASON: 'group:reason',
  SOURCE: 'group:source',
  NO_CLOSURE_DETAIL: 'noClosureDetail',
};

/**
 * Longest option label the 350px closure dropdown renders on a single line.
 *
 * Agency-named closing systems are free text and run far longer than the partner keys
 * (CARTEGRAPH, CITY_WORKS), so they are capped here rather than left to MultiLevelDropdown's
 * ClippedText: its 50-char budget still overflows the dropdown, and it appends a "more" toggle
 * that reads as a second control inside the option row. Full names stay in the PDF summary,
 * which resolves labels from closureOptions rather than from these dropdown options.
 */
export const CLOSURE_LABEL_MAX_CHARS = 34;

export const truncateClosureLabel = (label: string): string =>
  label.length > CLOSURE_LABEL_MAX_CHARS ? `${label.slice(0, CLOSURE_LABEL_MAX_CHARS - 1).trimEnd()}…` : label;

export interface IParsedClosureFilters {
  closedReasonIds: number[];
  closedViaSourceIds: number[];
  includeNoClosureDetail: boolean;
}

const toId = (token: string, prefix: string): number | null => {
  const raw = token.slice(prefix.length);
  const id = Number(raw);

  return raw !== '' && Number.isFinite(id) ? id : null;
};

/**
 * Decode the flat closureFilters value array into the three CSList query params, named to match
 * the closure filtering contract the API established in CDS-7242. Parent group tokens (group:*)
 * and any malformed tokens are ignored so only concrete selections reach the server.
 */
export const parseClosureFilters = (closureFilters?: string[]): IParsedClosureFilters => {
  const closedReasonIds: number[] = [];
  const closedViaSourceIds: number[] = [];
  let includeNoClosureDetail = false;

  (closureFilters || []).forEach((token) => {
    if (typeof token !== 'string') {
      return;
    }

    if (token === CLOSURE_GROUP.NO_CLOSURE_DETAIL) {
      includeNoClosureDetail = true;
    } else if (token.startsWith(CLOSURE_PREFIX.REASON)) {
      const id = toId(token, CLOSURE_PREFIX.REASON);
      if (id !== null) {
        closedReasonIds.push(id);
      }
    } else if (token.startsWith(CLOSURE_PREFIX.SOURCE)) {
      const id = toId(token, CLOSURE_PREFIX.SOURCE);
      if (id !== null) {
        closedViaSourceIds.push(id);
      }
    }
    // group:* parent tokens and anything unrecognized fall through and are ignored.
  });

  return { closedReasonIds, closedViaSourceIds, includeNoClosureDetail };
};
